import { formatCompactAmount } from '@app/utils';
import { LimitConfig } from '../../types';

export const THUMB_SIZE = 28;
/** Rounded-rect thumb corner radius (Figma: 6). */
export const THUMB_BORDER_RADIUS = 6;
/** Grip lines inside the slider thumb handle. */
export const THUMB_GRIP_LINE_COLOR = '#8F8F8F66';
export const SLIDER_HEIGHT = 48;
export const TRACK_HEIGHT = 4;
export const TRACK_TOP = (SLIDER_HEIGHT - TRACK_HEIGHT) / 2;
export const THUMB_TOP = (SLIDER_HEIGHT - THUMB_SIZE) / 2;
export const RATE_ZONE_HEIGHT = 72;
export const SLIDER_BLOCK_HEIGHT = TRACK_TOP + RATE_ZONE_HEIGHT;

/** Gradient pair: solid → same color at 0% opacity (#RRGGBB00). */
export const toFadeGradient = (color: string): readonly [string, string] => {
  const base =
    color.startsWith('#') && color.length === 9 ? color.slice(0, 7) : color;
  return [base, `${base}00`] as const;
};

/** Thumb left edge at min — half the thumb sits past the track start. */
export const getThumbMinPosition = () => -THUMB_SIZE / 2;

/** Thumb left edge at max — half the thumb sits past the track end. */
export const getThumbMaxPosition = (trackWidth: number) =>
  trackWidth - THUMB_SIZE / 2;

export const formatSliderLabel = (amount: number, roundDown = false): string =>
  formatCompactAmount(amount, { prefix: '₹', roundDown });

export const getAmountFromPosition = (
  position: number,
  config: LimitConfig,
  trackWidth: number,
) => {
  const minPos = getThumbMinPosition();
  const maxPos = getThumbMaxPosition(trackWidth);
  const travel = maxPos - minPos;
  if (travel <= 0) return config.minAmount;

  const percentage = Math.max(0, Math.min(1, (position - minPos) / travel));

  if (percentage >= 1) return config.maxAmount;
  if (percentage <= 0) return config.minAmount;

  const raw =
    config.minAmount + percentage * (config.maxAmount - config.minAmount);
  const stepped = Math.round(raw / config.step) * config.step;
  return Math.min(Math.max(stepped, config.minAmount), config.maxAmount);
};

export const getPositionFromAmount = (
  amount: number,
  config: LimitConfig,
  trackWidth: number,
) => {
  const minPos = getThumbMinPosition();
  const maxPos = getThumbMaxPosition(trackWidth);
  const travel = maxPos - minPos;
  if (travel <= 0) return minPos;

  const range = config.maxAmount - config.minAmount;
  if (range <= 0) return minPos;

  const percentage = (amount - config.minAmount) / range;
  return minPos + percentage * travel;
};
